import {
  base64url,
  type CipherObject,
  canonical,
  MAX_CIPHER_BYTES,
  type ObjectScope,
  openObject,
  sealObject,
  unbase64url,
} from "./cryptoV2";

async function boundedStream(stream: ReadableStream<Uint8Array>, limit: number): Promise<Uint8Array<ArrayBuffer>> {
  const reader = stream.getReader();
  const parts: Uint8Array[] = [];
  let total = 0;
  try {
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      total += part.value.length;
      if (total > limit) {
        await reader.cancel();
        throw new Error("sync-decompression-limit");
      }
      parts.push(part.value);
    }
  } finally {
    reader.releaseLock();
  }
  const bytes = new Uint8Array(total);
  let at = 0;
  for (const part of parts) {
    bytes.set(part, at);
    at += part.length;
  }
  return bytes;
}

export async function packEncrypted(
  key: string | CryptoKey,
  scope: ObjectScope,
  value: unknown,
  limit: number,
): Promise<CipherObject> {
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 1024 * 1024) throw new Error("sync-batch-limit");
  const bytes = new TextEncoder().encode(canonical(value));
  if (bytes.length > limit) throw new Error("sync-batch-limit");
  if (typeof CompressionStream !== "function") throw new Error("sync-compression-unavailable");
  const compressed = await boundedStream(
    new Blob([bytes]).stream().pipeThrough(new CompressionStream("gzip")),
    MAX_CIPHER_BYTES,
  );
  const cipher = await sealObject(key, scope, {
    codec: "gzip",
    uncompressedBytes: bytes.length,
    bytes: base64url(compressed),
  });
  if (new TextEncoder().encode(canonical(cipher)).length > MAX_CIPHER_BYTES) throw new Error("sync-file-limit");
  return cipher;
}

export async function unpackEncrypted(
  key: string | CryptoKey,
  scope: ObjectScope,
  cipher: CipherObject,
  limit: number,
): Promise<unknown> {
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 1024 * 1024) throw new Error("sync-decompression-limit");
  const value = (await openObject(key, scope, cipher)) as {
    codec: unknown;
    uncompressedBytes: unknown;
    bytes: unknown;
  };
  if (
    !value ||
    Object.keys(value).sort().join() !== "bytes,codec,uncompressedBytes" ||
    value.codec !== "gzip" ||
    !Number.isSafeInteger(value.uncompressedBytes) ||
    Number(value.uncompressedBytes) < 0 ||
    Number(value.uncompressedBytes) > limit ||
    typeof value.bytes !== "string"
  )
    throw new Error("sync-decompression-limit");
  if (typeof DecompressionStream !== "function") throw new Error("sync-compression-unavailable");
  const compressed = unbase64url(value.bytes, MAX_CIPHER_BYTES);
  const bytes = await boundedStream(
    new Blob([compressed]).stream().pipeThrough(new DecompressionStream("gzip")),
    Number(value.uncompressedBytes),
  );
  if (bytes.length !== value.uncompressedBytes) throw new Error("sync-decompression-limit");
  return JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
}
